/**
 * SuggestedOrderCard — one confirmable order suggestion for the create-case flow.
 *
 * @description
 *   Renders a single detected/suggested Shopify order (name, customer, date, a compact
 *   line-item summary) with a primary "adopt this order" action and a secondary "different
 *   order affected" escape. Surfaces the reliable inbox `suggested_orders` as a one-click
 *   confirm instead of a blank search, on both the match-step and the ctx=null InboxCard path.
 *
 * @useWhen
 *   - A create-case flow has a detected order to confirm before committing
 *   - Replacing a blank order-search with a top suggestion + "different order" escape
 * @dontUseFor
 *   - Multi-order pickers (use the search list) — this shows exactly one suggestion
 * @related
 *   - RMANewCaseWizardContent order search — the fallback when the user rejects the suggestion
 * @props
 *   order: suggested_orders[] element · onAccept(order) · onReject() · confidence?: number ·
 *   detectedFromEmail?: bool (shows a "detected from email" chip)
 */
import React from 'react'
import { Box, Stack, Typography, Chip, Button } from '@mui/material'
import LinkIcon from '@mui/icons-material/Link'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import { SP, PANEL_SX } from '../theme/tokens'
import CaseTime from '../components/common/CaseTime'
import { parseTimestamp } from '../utils/time'

export default function SuggestedOrderCard({ order, onAccept, onReject, confidence, detectedFromEmail }) {
  if (!order) return null
  const items = order.line_items || []
  const firstTitle = items[0]?.title
  const more = items.length > 1 ? ` +${items.length - 1}` : ''
  // Track A5-4b: the Shopify order's creation day, as a record fact. The
  // parse-first gate is kept (was `isNaN(d.getTime())`) so an unparseable value
  // still renders NO caption rather than a placeholder dash — same parser as
  // the render, so the gate and the output can never disagree.
  const createdLabel = parseTimestamp(order.created_at)
    ? <CaseTime value={order.created_at} variant="absolute" precision="date" />
    : null
  return (
    <Box sx={{ ...PANEL_SX, borderColor: 'primary.light' }}>
      <Stack direction="row" alignItems="center" spacing={SP.tight} sx={{ mb: SP.tight }} flexWrap="wrap">
        <LinkIcon fontSize="small" color="action" />
        <Chip label={order.name} size="small" />
        {createdLabel && (
          <Typography variant="caption" color="text.secondary">{createdLabel}</Typography>
        )}
        {detectedFromEmail && (
          <Chip icon={<AutoFixHighIcon />} label="detected from email" size="small" color="primary" variant="outlined" />
        )}
        {typeof confidence === 'number' && confidence > 0 && (
          <Chip
            label={`${Math.round(confidence * 100)}%`}
            aria-label={`match confidence ${Math.round(confidence * 100)}%`}
            size="small"
            variant="outlined"
          />
        )}
      </Stack>
      {(order.customer_name || order.customer_email) && (
        <Typography variant="body2" color="text.secondary">
          {order.customer_name || order.customer_email}
        </Typography>
      )}
      {firstTitle && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {firstTitle}{more}
        </Typography>
      )}
      <Stack direction="row" spacing={SP.tight} sx={{ mt: SP.tight }}>
        <Button variant="contained" size="small" onClick={() => onAccept?.(order)}>Use this order</Button>
        {onReject && (
          <Button variant="text" size="small" color="inherit" onClick={() => onReject()}>Different order</Button>
        )}
      </Stack>
    </Box>
  )
}
